// Plays pre-recorded voiceover clips (from new_landing.exercise_strings.audio_url)
// instead of the browser's real-time SpeechSynthesis. Modeled on the
// AudioPlayer class in lgp_app_fe/src/services/audioPlayer.ts: a single
// shared HTMLAudioElement, playback resolves/rejects via a Promise so
// callers can chain "after the voiceover finishes" logic the same way they
// used to chain off SpeechSynthesisUtterance.onend.
class AudioPlayer {
  constructor() {
    this.currentAudio = null;
  }

  play(url) {
    this.stop();

    return new Promise((resolve, reject) => {
      if (!url) {
        reject(new Error('No audio url provided'));
        return;
      }

      const audio = new Audio(url);
      this.currentAudio = audio;

      const isCurrent = () => this.currentAudio === audio;
      const cleanup = () => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
      };
      const onEnded = () => {
        cleanup();
        if (isCurrent()) this.currentAudio = null;
        resolve();
      };
      const onError = (e) => {
        cleanup();
        // A clip that's already been superseded by a newer play()/stop()
        // call can still fire a trailing 'error' event (e.g. the browser
        // aborting its in-flight load) — that's not a real playback
        // failure, just fallout from being interrupted, so don't reject.
        if (!isCurrent()) { resolve(); return; }
        this.currentAudio = null;
        reject(e);
      };

      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onError);

      audio.play().catch((error) => {
        cleanup();
        if (!isCurrent()) { resolve(); return; }
        this.currentAudio = null;
        reject(error);
      });
    });
  }

  stop() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  }
}

export const audioPlayer = new AudioPlayer();
